import React, { lazy, Suspense, useState, useEffect } from "react";
import { SubHeading } from "../../shared/Components";
import CommonModal from "../../shared/Components/Modal/CommonModal";
import {
  AddEditUserModal,
  LoginHistoryModal,
  UserResetModal,
  ViewSharingModal,
} from "../../shared/Components/Modal";
import { useQuery } from "@tanstack/react-query";
import { getAxios } from "../../shared/Services/commonService";
import { API_USER } from "../../utils/api/ApiConstant";
import { useSelector } from "react-redux";
import {
  selectLoggedInUserId,
  selectLoggedInUsername,
} from "../../store/user/user-selector";
import Icon from "../../assets/icons/Icon";

const ManageUsersBody = lazy(() => import("./ManageUsersBody"));

const ManageUsers = () => {
  // selector
  const LoggedInUserId = useSelector(selectLoggedInUserId);
  const LoggedInUsername = useSelector(selectLoggedInUsername);

  // states
  const [userBreadCrumb, setUserBreadCrumb] = useState([]);
  console.log("🚀 ~ ManageUsers ~ userBreadCrumb:", userBreadCrumb);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isShow, setIsShow] = useState({
    isOpen: false,
    rowData: null,
    modalTitle: "",
    modalContent: null,
    size: "",
  });

  const { isLoading, data, refetch } = useQuery({
    queryKey: ["userData", LoggedInUserId, selectedUser],
    queryFn: async () =>
      await getAxios(API_USER, {
        userId: selectedUser,
      }),
    enabled: !!LoggedInUserId,
  });

  useEffect(() => {
    if (!LoggedInUserId || !LoggedInUsername) return;

    setSelectedUser(LoggedInUserId);
    setUserBreadCrumb([{ id: LoggedInUserId, userName: LoggedInUsername }]);
  }, [LoggedInUserId, LoggedInUsername]);

  const handleModal = ({
    rowData = {},
    title = "Edit",
    size = "xl",
    name = "",
    isFixedHeight = false,
  }) => {
    const modalContent =
      name === "upsert"
        ? AddEditUserModal
        : name === "loginHistory"
          ? LoginHistoryModal
          : name === "viewSharing"
            ? ViewSharingModal
            : name === "resetPassword"
              ? UserResetModal
              : "";

    if (!modalContent) return;

    setIsShow({
      isOpen: true,
      modalContent,
      modalTitle: title,
      rowData: { ...rowData },
      size,
      isFixedHeight,
    });
  };

  const handleUserBreadCrumb = (rowData, isOnTableClick = true) => {
    setSelectedUser(rowData?.id);

    setUserBreadCrumb((prev) => {
      if (isOnTableClick) {
        const existingIndex = prev.findIndex((item) => item.id === rowData.id);

        // If already exists, keep only up to that item
        if (existingIndex !== -1) {
          return prev.slice(0, existingIndex + 1);
        }

        // Append new breadcrumb
        return [...prev, rowData];
      }

      // Breadcrumb click
      const index = prev.findIndex((item) => item.id === rowData.id);

      return index !== -1 ? prev.slice(0, index + 1) : prev;
    });
  };

  return (
    <>
      <SubHeading
        isAdd={true}
        handleShowHide={() =>
          handleModal({ title: "create", isFixedHeight: true, name: "upsert" })
        }
      />
      <ol className="breadcrumb-list">
        {userBreadCrumb?.map((item, index) => {
          const isLast = index === userBreadCrumb.length - 1;

          return (
            <li
              key={item.id}
              className={`breadcrumb-item ${isLast ? "active" : ""}`}
            >
              {isLast ? (
                <span className="breadcrumb-current" aria-current="page">
                  {item.userName}
                </span>
              ) : (
                <button
                  type="button"
                  className="breadcrumb-link"
                  onClick={() => handleUserBreadCrumb(item, false)}
                >
                  {item.userName}
                </button>
              )}

              {!isLast && <span className="icon_font">/</span>}
            </li>
          );
        })}
      </ol>

      <Suspense fallback={<div>Loading...</div>}>
        <ManageUsersBody
          handleModal={handleModal}
          data={data}
          isLoading={isLoading}
          refetch={refetch}
          handleUserBreadCrumb={handleUserBreadCrumb}
          selectedUser={selectedUser}
        />
      </Suspense>

      {isShow?.isOpen && (
        <CommonModal
          isShow={isShow.isOpen}
          size={isShow?.size || "md"}
          backdrop="static"
          handleModalHide={() =>
            setIsShow((prev) => ({
              ...prev,
              isOpen: false,
              rowData: {},
            }))
          }
          modalTitle={isShow.modalTitle}
          isFixedHeight={isShow?.isFixedHeight || false}
        >
          {isShow.modalContent &&
            React.createElement(isShow.modalContent, {
              handleModalHide: () =>
                setIsShow((prev) => ({
                  ...prev,
                  isOpen: false,
                  rowData: {},
                  modalTitle: "",
                })),
              rowData: isShow.rowData,
              fetchData: refetch,
            })}
        </CommonModal>
      )}
    </>
  );
};

export default ManageUsers;
